import * as Dialog from '@radix-ui/react-dialog';
import { useRef } from 'react';
import styled, { keyframes } from 'styled-components';

import { translate } from '../../common/helpers/translate.helper';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import { Button } from '../Button/Button';
import { Text } from '../Text/Text';

import type { DrawerConfirmationOptions } from './layerStoreReducer';

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const ConfirmationSurface = styled(Dialog.Content)`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${theme.space[32]};
    background-color: ${theme.colors.background.default}e6;
    backdrop-filter: blur(4px);
    border-radius: inherit;
    animation: ${fadeIn} 120ms ease-out both;
    overflow: auto;
`;

interface ConfirmationDialogProps {
    confirmation: DrawerConfirmationOptions;
    onKeepEditing: () => void;
    onConfirm: () => void;
    restoreFocus?: () => void;
}

export const ConfirmationDialog = ({
    confirmation,
    onKeepEditing,
    onConfirm,
    restoreFocus,
}: ConfirmationDialogProps) => {
    const keepEditingRef = useRef<HTMLButtonElement>(null);
    const previousFocus = useRef(document.activeElement);

    return (
        <Dialog.Root open>
            <ConfirmationSurface
                onOpenAutoFocus={(event) => {
                    event.preventDefault();
                    keepEditingRef.current?.focus();
                }}
                onCloseAutoFocus={(event) => {
                    event.preventDefault();
                    if (restoreFocus) {
                        restoreFocus();
                        return;
                    }
                    if (previousFocus.current instanceof HTMLElement && previousFocus.current.isConnected) {
                        previousFocus.current.focus();
                    }
                }}
                onEscapeKeyDown={(event) => event.preventDefault()}
                onInteractOutside={(event) => event.preventDefault()}
            >
                <Box display="grid" gap={16} maxWidth={400} width="100%">
                    <Dialog.Title asChild>
                        <Text as="h2" variant="title">
                            {confirmation.title}
                        </Text>
                    </Dialog.Title>
                    <Dialog.Description asChild>
                        <Text as="p" variant="body" color="foreground.muted">
                            {confirmation.description}
                        </Text>
                    </Dialog.Description>
                    <Box display="flex" gap={8}>
                        <Button ref={keepEditingRef} type="button" onClick={onKeepEditing}>
                            {translate('drawer.keepEditing')}
                        </Button>
                        <Button
                            type="button"
                            emphasis="high"
                            destructive={confirmation.destructive}
                            onClick={onConfirm}
                        >
                            {confirmation.confirmText}
                        </Button>
                    </Box>
                </Box>
            </ConfirmationSurface>
        </Dialog.Root>
    );
};
